<template>
  <ResponsiveLayout :progress="progress">
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>
    <div class="kds-main-wrapper prescription-guidance">
      <div class="kds-content-block">
        <p
          class="kds-typography-title-xlarge"
          data-qa="header"
          v-html="headerText"
        />
        <p
          class="kds-typography-paragraph-large"
          data-qa="description"
          v-html="descriptionText"
        />
      </div>
      <div
        ref="spinnerRef"
        class="prescription-guidance__spinner"
      >
        <Base64Spinner />
      </div>
      <img
        :src="pdfFileViewUrl"
        class="prescription-guidance__pdf"
        data-qa="prescription-guidance-pdf"
        @load="handlePdfLoadEvent"
      />

      <div
        v-if="showPdfDownloadInstructions"
        class="kds-content-block"
      >
        <div>
          <div
            class="prescription-guidance__section"
            v-html="$t('eu__prescription_guidance__instructions__save')"
          />
          <ul class="prescription-guidance__list">
            <li
              v-html="
                $t('eu__prescription_guidance__instructions__item_selection')
              "
            />
            <li
              v-html="
                $t(
                  'eu__prescription_guidance__instructions__dest_path_selection',
                )
              "
            />
            <li
              v-html="
                $t(
                  'eu__prescription_guidance__instructions__dest_path_check_in',
                )
              "
            />
          </ul>
        </div>
        <div>
          <div
            class="prescription-guidance__section"
            v-html="$t('eu__prescription_guidance__instructions__print')"
          />
          <ul class="prescription-guidance__list">
            <li
              v-html="
                $t('eu__prescription_guidance__instructions__item_selection')
              "
            />
            <li
              v-html="
                $t('eu__prescription_guidance__instructions__share_option')
              "
            />
            <li
              v-html="
                $t('eu__prescription_guidance__instructions__print_option')
              "
            />
          </ul>
        </div>
      </div>
      <BaseButton
        v-else
        :disabled="interactiveButtonState === 'DISABLED'"
        :text="$t('eu__insurance_prescription__public__pdf_cta_v9.4.0')"
        @click="handleDownloadPdfEvent"
      />
      <div
        v-if="showPrescriptionCheckFooter"
        class="kds-content-block"
        data-qa="ctrls"
      >
        <BaseButton
          :text="ctaButtonText"
          data-qa="submit"
          @click="handleSubmitEvent"
        />
        <BaseButton
          :text="secondaryCtaButtonText"
          data-qa="cancel"
          @click="handleCancelEvent"
        />
      </div>
    </div>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref } from 'vue'
import useUserStore from '@shared/stores/useUserStore.js'
import { isFromApp } from '@shared/helpers/isFromApp'
import { canReturnToApp } from '@eu/app/helpers/canReturnToApp'
import { useI18n } from 'petite-vue-i18n'
import env from '@shared/env'
import useDeepLink, {
  DEEP_LINK_CONFIGS,
} from '@shared/composables/useDeepLink.js'
import guideCOPDEnPng from '@eu/app/assets/png/Kaia_COPD_doctors_guide_en.png'
import guideCOPDDePng from '@eu/app/assets/png/Kaia_COPD_doctors_guide_de.png'
import guideCOPDEnSvg from '@eu/app/assets/svg/Kaia_COPD_doctors_guide_en.svg'
import guideCOPDDeSvg from '@eu/app/assets/svg/Kaia_COPD_doctors_guide_de.svg'
import guideMSKEnPng from '@eu/app/assets/png/Kaia_MSK_doctors_guide_en.png'
import guideMSKDePng from '@eu/app/assets/png/Kaia_MSK_doctors_guide_de.png'
import guideMSKEnSvg from '@eu/app/assets/svg/Kaia_MSK_doctors_guide_en.svg'
import guideMSKDeSvg from '@eu/app/assets/svg/Kaia_MSK_doctors_guide_de.svg'
import BaseButton from '@shared/components/BaseButton.vue'
import Base64Spinner from '@shared/components/Base64Spinner.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import * as apiRequest from '@shared/api-endpoints/index.js'
import Tracker from '@shared/Tracker.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'

const { t } = useI18n()
const userStore = useUserStore()
const corporateStore = useCorporateStore()
const { progress } = useOnboardingProgress()
const { navigateBack, canGoBack } = useNavigation(ROUTE.PRESCRIPTION_GUIDANCE)

const interactiveButtonState = ref('ACTIVE')
const spinnerRef = ref(null)

const corporate = computed(() => corporateStore.corporate)
const lang = computed(() => userStore.lang)
const appName = computed(() => t('eu__generic__app'))

const fileMap = {
  COPD_en_svg: guideCOPDEnSvg,
  COPD_en_png: guideCOPDEnPng,
  COPD_de_svg: guideCOPDDeSvg,
  COPD_de_png: guideCOPDDePng,
  MSK_en_svg: guideMSKEnSvg,
  MSK_en_png: guideMSKEnPng,
  MSK_de_svg: guideMSKDeSvg,
  MSK_de_png: guideMSKDePng,
}

const pdfFileViewUrl = computed(() => {
  const l = lang.value.indexOf('en') === 0 ? 'en' : 'de'
  const type = isFromApp() ? 'png' : 'svg'
  return fileMap[`${env.VITE_DISEASE.toUpperCase()}_${l}_${type}`]
})

const showPdfDownloadInstructions = computed(() => isFromApp())
const headerText = computed(() => t('eu__prescription_guidance__header'))
const descriptionText = computed(() =>
  t('eu__prescription_guidance__description', { app_name: appName.value }),
)
const showPrescriptionCheckFooter = computed(() => canReturnToApp())
const ctaButtonText = computed(() =>
  !env.features.diga
    ? t('eu__prescription_check__primary_cta__diga_disabled')
    : t('eu__prescription_check__primary_cta'),
)
const secondaryCtaButtonText = computed(() =>
  !env.features.diga
    ? t('eu__prescription_check__secondary_cta__diga_disabled')
    : t('eu__prescription_check__secondary_cta'),
)

const { getDeepLink } = useDeepLink(DEEP_LINK_CONFIGS.activationProgress)

const handleDownloadPdfEvent = () => {
  interactiveButtonState.value = 'PROCESSING'
  fetch(pdfFileViewUrl.value).then((response) => {
    response.text().then((bits) => {
      const pdfUrl = `data:application/octet-stream;base64,${btoa(bits)}`
      const link = document.createElement('a')
      const l = lang.value.indexOf('en') === 0 ? 'en' : 'de'
      const pdfFileName = `${env.VITE_DISEASE}_Doctor_${l}.svg`
      link.setAttribute('href', pdfUrl)
      link.setAttribute('download', pdfFileName)
      link.click()
      interactiveButtonState.value = 'ACTIVE'
    })
  })
}

const handlePdfLoadEvent = () => {
  if (spinnerRef.value?.parentNode != null) {
    spinnerRef.value.parentNode.removeChild(spinnerRef.value)
  }
}

const handleSubmitEvent = async () => {
  Tracker.trackButtonSelect(
    'user_has_prescription',
    'signup_get_diga_prescription_info',
  )
  apiRequest.sendSubscriptionActivationEntry({
    type: 'user_has_prescription',
  })
  const deepLink = await getDeepLink()
  window.location.redirectToDeeplink(deepLink)
}

const handleCancelEvent = async () => {
  const deepLink = await getDeepLink()
  window.location.redirectToDeeplink(deepLink)
}
</script>

<style lang="scss">
.prescription-guidance {
  &__spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 64px;
    height: 64px;
    margin: -32px 0 0 -32px;
  }

  &__section {
    @include kds-typography-title-medium;
    color: $kds-color-text-tertiary;
  }

  &__list {
    @include kds-typography-paragraph-large();
    margin: 0;
    padding-left: 16px;
    color: $kds-color-text-tertiary;
  }

  &__pdf {
    border: 2px solid $kds-color-background-accent;
  }
}
</style>
